import { scopeToBase } from '../PeriodTabs/periodTabs';

/**
 * Semesters as the periods of a course listing.
 *
 * A period here is the string "SS2014" or "WS2012". The year is always the
 * year the semester *starts* in, which is how the data already reads and how
 * the old site's folders were named: the winter semester carries the turn of
 * the year, so `/teaching/WS1213` holds the courses of WS 2012/13 and their
 * `course_year` is 2012. That is also what makes the ordering work - sorting
 * on (year, SS before WS) puts SS 2014 ahead of WS 2013/14 and behind
 * WS 2014/15, which is the order a reader expects.
 */

const YEAR = 'course_year';
const SEMESTER = 'course_semester';
const ANY = 'plone.app.querystring.operation.selection.any';

const parse = (period) => [period.slice(0, 2), period.slice(2)];

/** "WS2012" -> "WS 2012/13", "SS2014" -> "SS 2014". */
export const semesterLabel = (period) => {
  const [term, year] = parse(period);
  if (term !== 'WS') return `${term} ${year}`;
  const next = String(Number(year) + 1).slice(-2);
  return `WS ${year}/${next}`;
};

/** The folder that semester has: "WS2012" -> "WS1213", "SS2014" -> "SS14". */
export const semesterSegment = (period) => {
  const [term, year] = parse(period);
  const short = year.slice(-2);
  if (term !== 'WS') return `SS${short}`;
  return `WS${short}${String(Number(year) + 1).slice(-2)}`;
};

/** The reverse, for recognising which page we are on. */
export const semesterOfSegment = (segment) => {
  let m = /^SS(\d{2})$/.exec(segment || '');
  if (m) return `SS20${m[1]}`;
  m = /^WS(\d{2})(\d{2})$/.exec(segment || '');
  if (m) return `WS20${m[1]}`;
  return null;
};

const asList = (v) => (Array.isArray(v) ? v : v == null ? [] : [v]);
const token = (v) => (v && typeof v === 'object' ? v.token || v.title : v);

/** An item's semester, from its two fields. */
const periodOf = (item) => {
  const term = token(asList(item[SEMESTER])[0]) || '';
  const year = token(asList(item[YEAR])[0]) || '';
  return term && year ? `${term}${year}` : '';
};

/** Newest first: later year first, and within a year the winter semester. */
const compare = (a, b) => {
  const [ta, ya] = parse(a);
  const [tb, yb] = parse(b);
  if (ya !== yb) return ya < yb ? -1 : 1;
  if (ta === tb) return 0;
  return ta === 'SS' ? -1 : 1;
};

/**
 * Every semester the query could cover.
 *
 * Unlike the publications bar there is no vocabulary to fall back on, and
 * neither field names a semester on its own - a query saying "2014" and "SS"
 * means SS 2014, but a query naming several years and both terms means every
 * combination. So the candidates are the product of whatever the query names,
 * and the counts then throw away the combinations that hold nothing. Naming
 * neither field gives the full range the site covers.
 */
const FIRST_YEAR = 2008;
const periods = (querystring) => {
  const crit = (querystring?.query || []).reduce((acc, c) => {
    if (c.i === YEAR || c.i === SEMESTER) acc[c.i] = asList(c.v).map(String);
    return acc;
  }, {});
  const thisYear = new Date().getFullYear();
  const years = crit[YEAR]?.length
    ? crit[YEAR]
    : Array.from({ length: thisYear - FIRST_YEAR + 1 }, (_, i) =>
        String(FIRST_YEAR + i),
      );
  const terms = crit[SEMESTER]?.length ? crit[SEMESTER] : ['SS', 'WS'];
  const out = [];
  years.forEach((y) => terms.forEach((t) => out.push(`${t}${y}`)));
  return out.sort(compare).reverse();
};

/** The query, restricted to the given semesters. */
const narrow = (querystring, selection) => {
  const rest = { ...querystring };
  const kept = (rest.query || []).filter(
    (c) => c.i !== YEAR && c.i !== SEMESTER,
  );
  const years = [...new Set(selection.map((p) => parse(p)[1]))];
  const terms = [...new Set(selection.map((p) => parse(p)[0]))];
  return {
    ...rest,
    query: [
      ...kept,
      { i: YEAR, o: ANY, v: years },
      { i: SEMESTER, o: ANY, v: terms },
    ],
  };
};

/** On a semester page the folder already picks the semester. */
const withoutPeriod = (querystring) => ({
  ...querystring,
  query: (querystring.query || []).filter(
    (c) => c.i !== YEAR && c.i !== SEMESTER,
  ),
});

const courseSemesterSpec = {
  name: 'courseSemesters',
  param: 'semester',
  navLabel: 'Semesters',
  periods,
  narrow,
  // Counting on a semester page has to look past that page's own folder, or
  // the bar would have a single tab.
  scope: scopeToBase,
  withoutPeriod,
  of: periodOf,
  label: semesterLabel,
  compare,
  segment: semesterSegment,
  period: semesterOfSegment,
};

export default courseSemesterSpec;
